import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChildren, QueryList } from '@angular/core';
import { Router } from '@angular/router';
import { Table } from 'primeng/table';
import * as dayjs from 'dayjs';
import { ModalController } from '@ionic/angular';
import { FormatAddress, GoogleMapsUrl } from '../../../services/address.util';
import { QuickCommentComponent } from '../../comments/quick-add/quick-add.component';
import { ApiService } from '../../../api/api.service';
import { SearchService } from '../../../api/search.service';
import { CreatePage } from '../../../companies/create/create.page';
import { DetailsPage } from '../../../companies/details/details.page';
import { SearchComponent } from '../../search/search.component';
import { DefaultFolderStructurePage } from 'src/app/modals/default-folder-structure/default-folder-structure.page';
import { CompaniesContactsSelectPage } from 'src/app/modals/companies-contacts-select/companies-contacts-select.page';
import { GlobalService } from 'src/app/api/global.service';
import { SelectPage } from 'src/app/companies/select/select.page';
import { ManageComponent } from 'src/app/components/tags/manage/manage.component';
import { ManagerComponent } from '../../groups/manager/manager.component';
import { SelectComponent as TagsSelectPage } from 'src/app/components/tags/select/select.component';
import { ImportPage } from 'src/app/companies/import/import.page';
import { DeletedCompaniesPage } from 'src/app/modals/deleted-companies/deleted-companies.page';
import { CookieService } from 'ngx-cookie-service';
import { ColumnPickerComponent, ColumnConfig } from '../../tables/column-picker/column-picker.component';



@Component({
  selector: 'app-companies-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
})
export class TableComponent implements OnInit, OnChanges {



  loaded = false;
  skeleton = false;

  tags: any[];
  team_members: any[];
  groups: any[];

  matchModeOptions = [];

  hasSearch = false;
  is_searching = false;
  @Input() companies: any = null;
  non_grouped_companies: any[] = [];
  loaded_companies = false;
  @Input() show_paging: any = null;
  @Input() hide_search = false;
  @Input() hide_create = false;
  @Input() url_mod = '';
  @Input() allow_hash_set = true;
  /**
   * Array of column keys to hide. Example: ['tags','groups'] to hide tags and groups columns
   */
  @Input() hideColumns: string[] = [];
  @Input() hide_column_picker = false;
  @Input() groupView = true;
  @Input() hideEmptyGroups = false;
  @Input() resetQuickSearchToken = 0;

  /**
   * Column configuration for the column picker
   */
  availableColumns: ColumnConfig[] = [
    { key: 'name', label: 'Company Name', visible: true },
    { key: 'team_member_role', label: 'Team Member Role', visible: true },
    { key: 'business_number', label: 'Business Number', visible: true },
    { key: 'custom_id', label: 'Custom ID', visible: true },
    { key: 'address', label: 'Address', visible: false },
    { key: 'year_end_date', label: 'Year End Date', visible: true },
    { key: 'groups', label: 'Groups', visible: true },
    { key: 'tags', label: 'Tags', visible: true },
    { key: 'team_members', label: 'Team Members', visible: true },
    // Hidden until turned on in the column picker
    { key: 'corp_name', label: 'Corporate Name', visible: false },
    { key: 'type', label: 'Type', visible: false },
    { key: 'industry', label: 'Industry', visible: false },
    { key: 'fax', label: 'Fax', visible: false },
    { key: 'website', label: 'Website', visible: false },
  ];

  // Enhanced search functionality
  all_companies: any[] = []; // Store original unfiltered companies
  current_search_query: any = null;
  quickSearchTerm = '';
  // What is typed in the box (shown immediately); quickSearchTerm is what the table is actually filtered by, which
  // follows it ~250ms after typing stops so the table isn't re-filtered on every keystroke
  quickSearchInput = '';
  private quickSearchTimer: any = null;
  // The lowercase text each row is searched in - built once per row instead of on every pass over the table
  private searchTextCache = new WeakMap<object, string>();
  show_all_companies = true;
  non_grouped_objects = [];
  non_grouped_expanded = false;

  @ViewChildren(Table) tables: QueryList<Table>;
  activeCompanyFilterLabels: string[] = [];
  // Not private - table.component.html binds this into app-filter-presets' [fieldLabelMap].
  readonly companyFilterLabelMap: { [field: string]: string } = {
    name: 'Name',
    team_member_role: 'Role',
    groups: 'Groups',
    tags: 'Tags',
    team_members: 'Team Members',
  };

  onCompanyTableFilter(event: any) {
    this.activeCompanyFilterLabels = this.computeActiveFilterLabels(event.filters, this.companyFilterLabelMap);
  }

  clearCompanyFilters() {
    this.tables?.forEach(t => this.clearTableFilters(t));
    this.activeCompanyFilterLabels = [];
  }

  // Saved filter presets (see FilterPresetsComponent / components/projects/table's
  // GetCurrentTableFilters for the pattern this mirrors) - bundles the PrimeNG column filters with the
  // "above the table" toggle buttons (Show All/Just Client Companies, Show/Hide Empty Groups). Group
  // View itself is excluded on purpose - it's a display mode, not a filter.
  GetCurrentTableFilters = (): any => {
    return {
      columnFilters: (this.tables?.first as any)?.filters ?? null,
      toolbar: {
        show_all_companies: this.show_all_companies,
        hideEmptyGroups: this.hideEmptyGroups,
      }
    };
  };

  ApplyTableFilters(saved: any) {
    const isWrapped = !!saved && (('columnFilters' in saved) || ('toolbar' in saved));
    const columnFilters = isWrapped ? saved?.columnFilters : saved;
    const toolbar = isWrapped ? saved?.toolbar : null;

    const applied = columnFilters ? JSON.parse(JSON.stringify(columnFilters)) : {};
    this.tables?.forEach(t => {
      (t as any).filters = JSON.parse(JSON.stringify(applied));
      (t as any)._filter();
    });
    this.activeCompanyFilterLabels = this.computeActiveFilterLabels(applied, this.companyFilterLabelMap);

    if (toolbar) {
      if (typeof toolbar.show_all_companies === 'boolean') this.show_all_companies = toolbar.show_all_companies;
      if (typeof toolbar.hideEmptyGroups === 'boolean') this.hideEmptyGroups = toolbar.hideEmptyGroups;
      this.UpdateGroupsCount();
      this.UpdateFilterCookies();
    }
  }

  DescribeToolbarState = (toolbar: any): string[] => {
    const parts: string[] = [];
    parts.push(`Companies: ${toolbar.show_all_companies ? 'All Companies' : 'Just Client Companies'}`);
    if (typeof toolbar.hideEmptyGroups === 'boolean') {
      parts.push(`Empty Groups: ${toolbar.hideEmptyGroups ? 'Hidden' : 'Shown'}`);
    }
    return parts;
  };

  private clearTableFilters(t: Table): void {
    if (!t) return;
    (t as any).tableService.onResetChange();
    if ((t as any).isStateful()) (t as any).clearState();
    (t as any)._filter();
  }

  private computeActiveFilterLabels(filters: any, labelMap: { [field: string]: string }): string[] {
    if (!filters) return [];
    const active: string[] = [];
    for (const field of Object.keys(labelMap)) {
      const raw = filters[field];
      if (!raw) continue;
      const constraints = Array.isArray(raw) ? raw : [raw];
      const hasValue = constraints.some((c: any) => {
        if (c.value === null || c.value === undefined) return false;
        if (Array.isArray(c.value)) return c.value.length > 0;
        if (typeof c.value === 'string') return c.value.trim().length > 0;
        return true;
      });
      if (hasValue) active.push(labelMap[field]);
    }
    return active;
  }

  first_rule = {
    field: 'Company Name',
    opr: 'equals',
    value: '',
    type: 'string'
  }

  fields = [
    {
      name: 'Company Name',
      type: 'string'
    },
    {
      name: 'Groups',
      type: 'category',
      options: {

      }
    },
    {
      name: 'Team Members',
      type: 'category',
      options: {

      }
    },
    {
      name: 'Tags',
      type: 'category',
      options: {

      }
    }
  ];

  max_per_page = 100;


  inc_data = false;


  constructor(private apiService: ApiService, private modalController: ModalController, public globalService: GlobalService, private searchService: SearchService,private cookieService: CookieService, private router: Router) {
    this.matchModeOptions = [
      {
        label: 'Includes Any',
        value: "includesAny"
      },
      {
        label: 'Includes All',
        value: "includesAll"
      }
    ];
  }

  ngOnInit() {
    if(this.companies) this.inc_data = true;
    this.RestoreFilterCookies();
    this.initializeColumnVisibility();
    this.GetAllData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['resetQuickSearchToken'] && !changes['resetQuickSearchToken'].firstChange) {
      this.onQuickSearchChange('');
    }
  }

  /**
   * Initialize column visibility based on hideColumns input
   */
  initializeColumnVisibility() {
    // Columns hidden until the user turns them on in the column picker - the saved hideColumns (or the
    // parent's) predates them, so they'd otherwise just appear.
    const hidden = this.defaultHiddenColumns.filter(key =>
      !this.shownDefaultHiddenColumns.includes(key) && !this.hideColumns.includes(key));
    if (hidden.length) this.hideColumns = [...this.hideColumns, ...hidden];
    this.syncColumnsFromHideColumns();
  }

  // Columns that start hidden (see initializeColumnVisibility), and which of them the user has turned on.
  private readonly defaultHiddenColumns = ['address', 'corp_name', 'type', 'industry', 'fax', 'website'];
  private shownDefaultHiddenColumns: string[] = [];

  CompanyAddress(company: any): string {
    return FormatAddress(company?.str_address_1, company?.city, company?.province, company?.postal_code, company?.country);
  }

  CompanyMapsUrl(company: any): string {
    return GoogleMapsUrl(this.CompanyAddress(company));
  }

  // Puts the columns in the order the column picker left them (saved with the other table preferences). Columns the
  // saved order doesn't know about - added since - go after the known ones, in their default order.
  private ApplyColumnOrder(order: string[]) {
    const position = (key: string) => { const i = order.indexOf(key); return i < 0 ? order.length : i; };
    this.availableColumns = [...this.availableColumns].sort((a, b) => position(a.key) - position(b.key));
  }

  /**
   * Sync availableColumns visibility state from hideColumns array
   */
  syncColumnsFromHideColumns() {
    this.availableColumns.forEach(col => {
      col.visible = !this.hideColumns.includes(col.key);
    });
  }

  /**
   * Sync hideColumns array from availableColumns visibility state
   */
  syncHideColumnsFromColumns() {
    this.hideColumns = this.availableColumns
      .filter(col => !col.visible)
      .map(col => col.key);
    this.shownDefaultHiddenColumns = this.defaultHiddenColumns.filter(key => !this.hideColumns.includes(key));
  }

  // Admin-only (the Manage button itself is disabled for non-admins - see table.component.html) deep
  // link straight to the Companies section of Manage Firm, rather than always landing on Team Members
  // (see ManageFirmPage.ngOnInit's ?section= handling).
  GoToManageFirm() {
    this.router.navigate(['/manage-firm'], { queryParams: { section: 'companies' } });
  }

  /**
   * Open the column picker modal
   */
  async OpenColumnPicker() {
    const columnsCopy: ColumnConfig[] = this.availableColumns.map(col => ({ ...col }));

    const modal = await this.modalController.create({
      component: ColumnPickerComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        columns: columnsCopy
      },
      backdropDismiss: false
    });

    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.columns) {
        this.availableColumns = data.data.columns;
        this.syncHideColumnsFromColumns();
        this.UpdateFilterCookies();
      }
    });

    return await modal.present();
  }

  async GetAllData(force_refresh = false) {
    this.loaded_companies = false;
    this.GetTeamMembers();
    this.GetTags(force_refresh);
    await this.GetGroups(force_refresh);
    // Only default to 25 the first time (no saved preference yet) - don't clobber a restored one
    if (this.show_paging && !this.restored_max_per_page) this.max_per_page = 25;
    if (!this.inc_data) await this.Get(force_refresh);
    this.UpdateGroupsCount();
    this.loaded_companies = true;
  }

  // keep_search_cache is only true when the search text changed - any other call means the data may have changed,
  // so the cached search text of every row is dropped
  UpdateGroupsCount(keep_search_cache = false) {
    if (!keep_search_cache) this.searchTextCache = new WeakMap<object, string>();
    this.non_grouped_objects = [];
    this.non_grouped_companies = [];
    if (!this.companies) return;
    for (const company of this.companies) {
      if (!company.groups || company.groups.length === 0) {
        if (!this.show_all_companies && !company.is_a_client) continue;
        if (!this.MatchesQuickSearch(company)) continue;
        this.non_grouped_objects.push(company);
      }
    }
    if (!this.groups || !this.companies) {
      return;
    }
    for (const group of this.groups) {
      group.objects = [];
      for (const company of this.companies) {
        if (!this.show_all_companies && !company.is_a_client) continue;
        if (!this.MatchesQuickSearch(company)) continue;
        if (company.groups && Array.isArray(company.groups)) {
          if (company.groups.some(g => g.guid === group.guid)) {
            group.objects.push(company);
          }
        }
      }
      group.companies = this.GetFilteredCompanies(this.companies, group, false);
    }
    this.non_grouped_companies = this.GetFilteredCompanies(this.companies, null, true);
    for (const group of this.groups) {
      this.UpdateParentGroupsCount(group);
    }
  }

  UpdateParentGroupsCount(group) {

    if (!group.parent_guid) return;
    const parent_group = this.groups.find(g => g.guid === group.parent_guid);
    if (parent_group) {
      for (const obj of group.objects) {
        if (!parent_group.objects.some(o => o.guid === obj.guid)) {
          parent_group.objects.push(obj);
        }
      }
      if (parent_group.parent_guid) {
        this.UpdateParentGroupsCount(parent_group);
      }
    }
  }

  async Get(force_refresh = false) {
    this.companies = null;
    this.loaded = false;
    let data: any = null;
    if (this.url_mod == '') data = await this.apiService.GetCompanies(force_refresh);
    else data = await this.apiService.get('companies' + this.url_mod);
    if (data.success) {
      if (!data.data) data.data = [];
      // Store original companies and handle search state
      this.all_companies = [...data.data];

      // If we have an active search, reapply it to the new data
      if (this.is_searching && this.current_search_query) {
        const fieldMappingConfig = this.searchService.createCompanyFieldMapping();
        this.companies = this.searchService.filterDataByQuery(this.all_companies, this.current_search_query, fieldMappingConfig);
      } else {
        this.companies = [...this.all_companies];
        this.is_searching = false;
      }
    }
    if(!this.companies) this.companies = [];
    this.loaded = true;
    this.CheckHash();
    this.GetTeamMemberRoles();
    this.UpdateGroupsCount();
  }

  //This checks if the hash is set, if it is we want to show the details of that company
  async CheckHash() {
    if (!this.allow_hash_set) return;
    if (window.location.hash.replace('#', '')) {
      let hash_check = window.location.hash.replace('#', '');
      if (hash_check.length > 1) {
        let index = this.companies.findIndex(item => item.guid === hash_check);
        if (index >= 0) this.presentDetailsModal(this.companies[index]);
      }
    }
  }

  async GetCompanyDetails(company) {
    company.loaded_company_details = false;
    let company_details: any = await this.apiService.get(`companies/${company.guid}`);
    if (company_details.success) {
      company.clients = company_details.data[0].clients;
      company.loaded_company_details = true;
    }

  }

  async GetTaskDetails(task) {
    if (task.steps) return;
    task.loaded_task_details = false;
    let task_details: any = await this.apiService.get(`tasks/${task.guid}`);
    task.steps = task_details.data[0].steps;
    task.loaded_task_details = true;

  }

  async OpenCreateModal() {
    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal',
      backdropDismiss: false,
    })
    modal.onDidDismiss().then((data: any) => {
      // Local push, no re-fetch - group.companies/non_grouped_companies (what the table
      // actually renders) are only rebuilt by UpdateGroupsCount(), which is why the plain push
      // this used to do wasn't enough on its own (the new company sat in this.companies but
      // never made it into the rendered grouped view until something else, e.g. quick search,
      // happened to trigger a rebuild).
      if (data.data) {
        if (!this.companies) this.companies = [];
        this.companies.push(data.data);
        if (!this.all_companies) this.all_companies = [];
        this.all_companies.push(data.data);
        this.UpdateGroupsCount();
      }
    });
    return await modal.present()
  }

  async OpenImportModal() {
    const modal = await this.modalController.create({
      component: ImportPage,
      cssClass: 'large-modal',
      backdropDismiss: false
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.Get();
      }
    });
    return await modal.present()
  }

  GetCommentTooltip(item: any): string {
    if (!item.company_last_comment_date) return 'No comments yet — click to add';
    return `Last comment: ${dayjs(this.globalService.DateToUTCFormat(item.company_last_comment_date)).format('MMM D, YYYY h:mm A')}`;
  }

  async OpenAddComment(item: any) {
    const modal = await this.modalController.create({
      component: QuickCommentComponent,
      cssClass: 'small-modal',
      componentProps: { url: 'companies/comments', guid: item.guid, title: item.name },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.success) item.company_comment_count = (item.company_comment_count ?? 0) + 1;
  }

  async OpenQuickComment(item: any) {
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      componentProps: { company: item, activeIndex: 'comments' },
      backdropDismiss: false,
    });
    await modal.present();
  }

  async presentDetailsModal(company) {
    if (this.allow_hash_set) window.location.hash = company.guid;
    const modal = await this.modalController.create({
      component: DetailsPage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        company: company
      }
    })
    modal.onDidDismiss().then((data: any) => {
      window.location.hash = '';
      if (data.data) {
        if(data.data.deleted) this.GetAllData(true);
        else {
          this.GetCompanyInfo(company);
        }
      }


    });
    return await modal.present()

  }
  // Refreshes one company's row after its details were closed. Done quietly in the background (no blocking overlay - it used to
  // get stuck on screen) and in place, so the table keeps its page.
  async GetCompanyInfo(company) {
    try {
      const data: any = await this.apiService.get(`companies/${company.guid}`);
      if (data?.success && data.data?.[0]) {
        const existing = (this.companies || []).find(item => item.guid === company.guid);
        if (existing) Object.assign(existing, data.data[0]);
        const all_existing = (this.all_companies || []).find(item => item.guid === company.guid);
        if (all_existing && all_existing !== existing) Object.assign(all_existing, data.data[0]);
      }
    } catch (e) {
      console.error('Refreshing company failed', e);
    }
    this.UpdateGroupsCountKeepingPage();
  }

  // UpdateGroupsCount rebuilds the lists the tables show, which sends the paginators back to page 1 - put them back
  private UpdateGroupsCountKeepingPage() {
    const pages = (this.tables?.toArray() || []).map((t: any) => t.first);
    this.UpdateGroupsCount();
    setTimeout(() => {
      (this.tables?.toArray() || []).forEach((t: any, i: number) => {
        if (pages[i] != null && t.first !== pages[i]) { t.first = pages[i]; }
      });
    });
  }

  async GetGroups(force_refresh = false) {
    let data: any = await this.apiService.GetGroups('companies', force_refresh);
    this.groups = data.data;
    if (!this.groups) this.groups = [];
    this.UpdateGroupsCount();
  }
  async GetTeamMembers() {
    let data: any = await this.apiService.get('team_members');
    this.team_members = data.data;
    if (!this.team_members) this.team_members = [];
  }

  async GetTags(force_refresh = false) {
    let data: any = await this.apiService.GetTags('companies',force_refresh);
    this.tags = data.data;
    if (!this.tags) this.tags = [];
  }

  async TableSaved(event) {
    //Save the saved state to a database
    this.hasSearch = false;
    for (const key in event.filters) {
      if (!event.filters[key][0]) break;
      if (event.filters[key][0].value && event.filters[key][0].value != "") { this.hasSearch = true; }
    }
    //See if the search i
  }

  async OpenSearchModal() {
    const modal = await this.modalController.create({
      component: SearchComponent,
      cssClass: 'small-modal tall-modal',
      componentProps: {
        type: 'companies',
        show_date_range: false,
        get_groups: true,
        get_tags: true,
        get_status: false,
        get_team_members: true,
        first_rule: this.first_rule,
        fields: this.fields
      }
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.SearchCompanies(data.data.rules);
      }
    });
    return await modal.present()
  }

  async SearchCompanies(query) {
    this.is_searching = true;
    this.current_search_query = query;

    // Save current view and switch to table view for searching
    this.UpdateFilterCookies();
    this.groupView = false;

    // Use the search service to filter companies locally
    const fieldMappingConfig = this.searchService.createCompanyFieldMapping();
    const filteredCompanies = this.searchService.filterDataByQuery(this.all_companies, query, fieldMappingConfig);

    this.companies = filteredCompanies;
    this.UpdateGroupsCount();
  }

  async ClearSearch() {
    this.hasSearch = false;
    this.is_searching = false;
    this.current_search_query = null;
    if (this.all_companies.length > 0) {
      this.companies = [...this.all_companies];
    }
    // Restore view from cookie
    this.RestoreFilterCookies();
    this.UpdateGroupsCount();
  }

  async filter(val) {

  }




  @Input() show_team_member_role = false;
  @Input() team_member_guid = '';
  team_member_roles: any[] = [];
  async GetTeamMemberRoles() {
    let roles_data: any = await this.apiService.get('firms/team_members/roles');
    this.team_member_roles = roles_data.data;
    for (const company of this.companies) {
      if (company.team_members && Array.isArray(company.team_members)) {
        for (const team_member of company.team_members) {
          if (team_member.guid == this.team_member_guid) {
            const matchingRole = this.team_member_roles.find(role => role.guid === team_member.role_guid);
            company.team_member_role_title = matchingRole ? matchingRole.title : '';
            company.team_member_role_guid = matchingRole ? matchingRole.guid : '';
          }
        }
      }
    }
  }

  GetRoleColour(role_guid) {
    return this.team_member_roles.find(role => role.guid === role_guid)?.colour || '#000000';
  }

  // async onTeamMemberRoleChange(client: any, newRoleGuid: string) {
  //   let team_member = {
  //     guid: this.team_member_guid,
  //     role_id: '',
  //     role_guid: newRoleGuid
  //   }
  //   let data: any = await this.apiService.put(`companies/team_members/${client.guid}`, team_member);
  //   this.globalService.presentToastMessage(data.message, data.success);

  // }

  // Here we need to be able to select companies and add them to a team member
  async presentSelectCompaniesModal() {
    let companies_copy = JSON.stringify(this.companies);
    const modal = await this.modalController.create({
      component: CompaniesContactsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        selecting_for_team_member: true,
        hide_contacts: true,
        selected_companies: this.companies,
        remove_companies: this.companies,
        team_member_roles: this.team_member_roles,
        in_companies: [],
        target_header: 'Currently Selected'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        companies_copy = JSON.parse(companies_copy);
        this.CheckCompanies(companies_copy, data.data.companies);
      }
      else {
        this.companies = [...JSON.parse(companies_copy)];
        if(!this.companies) this.companies = [];
      }
    });
    return await modal.present();
  }

  // See if we have to remove or add companies for the team member
  async CheckCompanies(old_companies, new_companies) {
    await this.globalService.presentLoading("Updating Companies...", 9000000);
    // Remove companies that are no longer selected
    for (const company of old_companies) {
      const index = new_companies.findIndex(e => company.guid === e.guid);
      if (index == -1) {
        await this.RemoveCompanyFromTeamMember(company);
      }
    }
    // Add new companies that were not previously selected
    for (const company of new_companies) {
      const index = old_companies.findIndex(e => company.guid === e.guid);
      if (index == -1) {
        await this.AddCompanyToTeamMember(company);
      }
    }
    // Update companies if their data has changed
    for (const company of new_companies) {
      const index = old_companies.findIndex(e => company.guid === e.guid);
      if (index >= 0) {
        if (JSON.stringify(company) != JSON.stringify(old_companies[index])) {
          await this.UpdateCompanyForTeamMember(company);
        }
      }
    }
    this.globalService.dismissLoading();
    this.Get(); // Refresh companies list
  }

  // API calls to manage companies for the team member
  async RemoveCompanyFromTeamMember(company) {
    let team_member = {
      guid: this.team_member_guid,
      role_guid: company.team_member_role_guid
    }
    let data: any = await this.apiService.delete(`companies/team_members/${company.guid}`, team_member);
  }
  async AddCompanyToTeamMember(company) {
    let team_member = {
      guid: this.team_member_guid,
      role_guid: company.team_member_role_guid
    }
    let data: any = await this.apiService.post(`companies/team_members/${company.guid}`, team_member);
  }
  async UpdateCompanyForTeamMember(company) {
    let team_member = {
      guid: this.team_member_guid,
      role_guid: company.team_member_role_guid
    }
    let data: any = await this.apiService.put(`companies/team_members/${company.guid}`, team_member);
  }


  async OpenManageDefaultFolders() {
    const modal = await this.modalController.create({
      component: DefaultFolderStructurePage,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        for_clients: 0
      },
    })
    modal.onDidDismiss().then((data: any) => {

    });
    return await modal.present()
  }

  async OpenManageTags() {
    const modal = await this.modalController.create({
      component: ManageComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'tags',
        url: 'companies',
        local_only: true,
        title: "Manage Tags",
        name: "Tags",
      },
    })
    modal.onDidDismiss().then((data: any) => {

    });
    return await modal.present()
  }

  async OpenManageGroups() {
    const modal = await this.modalController.create({
      component: ManagerComponent,
      cssClass: 'tall-modal medium-modal',
      componentProps: {
        url_base: 'groups',
        url: 'companies',
        local_only: true,
        title: "Manage Groups",
        name: "Groups",
      },
    })
    modal.onDidDismiss().then((data: any) => {
      this.GetGroups();
    });
    return await modal.present()
  }


  CompanyInGroup(company, group) {

    if (!company.groups || company.groups.length == 0) return false;
    for (const g of company.groups) {

      if (g.guid === group.guid) {
        return true;
      }
    }
    return false;
  }


  async OpenDeletedModal() {
    const modal = await this.modalController.create({
      component: DeletedCompaniesPage,
      cssClass: 'medium-modal tall-modal',
      backdropDismiss: false,
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data && data.data.restored) this.GetAllData(true);
    });
    return await modal.present();
  }

  HideRowLogic(company, group, showNoGroup) {
    if (!this.groupView) {
      return (this.show_all_companies || company.is_a_client);
    }
    if (showNoGroup) {
      return (!company.groups || company.groups.length == 0) && (this.show_all_companies || company.is_a_client);
    }
    else if (!group) {
      return (this.show_all_companies || company.is_a_client);
    }
    else {
      return this.CompanyInGroup(company, group) && (this.show_all_companies || company.is_a_client);
    }
  }

  onQuickSearchChange(term: string) {
    this.quickSearchInput = term ?? '';
    if (this.quickSearchTimer) clearTimeout(this.quickSearchTimer);
    const apply = () => {
      this.quickSearchTimer = null;
      this.quickSearchTerm = this.quickSearchInput;
      this.UpdateGroupsCount(true);
    };
    // Clearing applies straight away, typing waits for a pause
    if (!this.quickSearchInput.trim()) apply();
    else this.quickSearchTimer = setTimeout(apply, 250);
  }

  private MatchesQuickSearch(company: any): boolean {
    const query = this.quickSearchTerm?.trim().toLowerCase();
    if (!query) return true;
    return this.SearchText(company).includes(query);
  }

  // Everything a company can be searched by, lowercased and joined (hidden columns included; email is searched too,
  // though it has no column). Built once per company and reused until the data changes (UpdateGroupsCount clears it).
  private SearchText(company: any): string {
    let text = this.searchTextCache.get(company);
    if (text !== undefined) return text;
    const year_end = company?.year_end_date ? dayjs(company.year_end_date).format('MMM D MMMM D') : '';
    text = [
      company?.name, company?.team_member_role_title, company?.business_number, company?.custom_id, this.CompanyAddress(company),
      year_end, company?.corp_name, company?.type, company?.industry, company?.fax, company?.website, company?.email,
      ...(company?.groups || []).map((g: any) => g.title),
      ...(company?.tags || []).map((t: any) => t.title),
      company?.all_team_members ? 'All Team Members' : '',
      ...(company?.team_members || []).map((m: any) => m.display_name),
    ].map(value => `${value ?? ''}`.toLowerCase()).join('\u0001');
    this.searchTextCache.set(company, text);
    return text;
  }

  // Names every column the quick search looks in
  get QuickSearchPlaceholder(): string {
    const columns = ['company name', ...(this.show_team_member_role ? ['team member role'] : []), 'business number', 'custom ID',
      'address', 'year end date', 'corporate name', 'type', 'industry', 'fax', 'website', 'groups', 'tags', 'team members'];
    return 'Quick search by ' + columns.join(', ') + '...';
  }

  // A saved website may have been typed without http(s):// - without it the link would be relative to this app
  WebsiteUrl(website: string): string {
    const url = (website || '').trim();
    return /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : 'https://' + url;
  }

  GetFilteredCompanies(companies: any[] | null | undefined, group: any, showNoGroup: boolean): any[] {
    const list = companies ?? [];
    return list.filter(c =>
      this.HideRowLogic(c, group, showNoGroup) &&
      this.MatchesQuickSearch(c) &&
      !c.filter_out_group &&
      !c.filter_out_tag &&
      !c.filter_out_team_member
    );
  }

  UpdateFilterCookies() {
    const filterPreferences = {
      groupView: this.groupView,
      show_all_companies: this.show_all_companies,
      hideEmptyGroups: this.hideEmptyGroups,
      hideColumns: this.hideColumns,
      columnOrder: this.availableColumns.map(col => col.key),
      shownDefaultHiddenColumns: this.shownDefaultHiddenColumns,
      max_per_page: this.max_per_page
    };
    this.cookieService.set('companyTableFilters', JSON.stringify(filterPreferences), 30, '/');
  }

  // Rows-per-page changed from the paginator's own dropdown - PrimeNG doesn't have a two-way [(rows)]
  // binding, so this is caught via (onPage) instead, same as any other filter change.
  private restored_max_per_page = false;
  OnTablePageChange(event: any) {
    if (event?.rows && event.rows !== this.max_per_page) {
      this.max_per_page = event.rows;
      this.UpdateFilterCookies();
    }
  }

  getTableDescription(): string {
    const parts: string[] = [];

    const companyLabel = this.show_all_companies ? 'all companies' : 'client companies';
    parts.push(`Showing ${companyLabel}`);

    if (this.is_searching) {
      parts.push('a search filter is active');
    }
    if (this.quickSearchTerm?.trim()) {
      parts.push(`Quick searching for: ${this.quickSearchTerm.trim()}`);
    }

    return parts.length === 1 ? parts[0] + '.' : parts[0] + ', ' + parts.slice(1).join(', ') + '.';
  }

  RestoreFilterCookies() {
    const cookieValue = this.cookieService.get('companyTableFilters');
    if (cookieValue) {
      try {
        const filterPreferences = JSON.parse(cookieValue);
        // Don't restore groupView when searching
        if (!this.is_searching && filterPreferences.groupView !== undefined) this.groupView = filterPreferences.groupView;
        if (filterPreferences.show_all_companies !== undefined) this.show_all_companies = filterPreferences.show_all_companies;
        if (filterPreferences.hideEmptyGroups !== undefined) this.hideEmptyGroups = filterPreferences.hideEmptyGroups;
        if (filterPreferences.hideColumns !== undefined) this.hideColumns = filterPreferences.hideColumns;
        if (Array.isArray(filterPreferences.shownDefaultHiddenColumns)) this.shownDefaultHiddenColumns = filterPreferences.shownDefaultHiddenColumns;
        if (Array.isArray(filterPreferences.columnOrder)) this.ApplyColumnOrder(filterPreferences.columnOrder);
        if (filterPreferences.max_per_page !== undefined) {
          this.max_per_page = filterPreferences.max_per_page;
          this.restored_max_per_page = true;
        }
      } catch (e) {
        console.error('Error parsing filter cookies:', e);
      }
    }
  }

  async OpenTagsModal(company: any) {
    if (!company.tags) company.tags = [];
    const tags_copy = JSON.stringify(company.tags);

    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'companies',
        selected_tags: company.tags
      }
    });

    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        const old_tags = JSON.parse(tags_copy);
        this.CheckCompanyTags(company, old_tags, data.data);
      } else {
        company.tags = JSON.parse(tags_copy);
      }
    });

    return await modal.present();
  }

  async CheckCompanyTags(company: any, old_tags: any[], new_tags: any[]) {
    await this.globalService.presentLoading('Updating Tags...', 9000000);
    old_tags.forEach(tag => {
      const index = new_tags.findIndex(e => tag.guid === e.guid);
      if (index == -1) {
        this.apiService.delete(`companies/tags/${company.guid}`, tag);
      }
    });
    new_tags.forEach(tag => {
      const index = old_tags.findIndex(e => tag.guid === e.guid);
      if (index == -1) {
        this.apiService.post(`companies/tags/${company.guid}`, tag);
      }
    });
    company.tags = [...new_tags];
    this.globalService.dismissLoading();
  }

  async OpenGroupsModal(company: any) {
    if (!company.groups) company.groups = [];
    const groups_copy = JSON.stringify(company.groups);

    const modal = await this.modalController.create({
      component: ManagerComponent,
      cssClass: 'medium-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'companies',
        selection_mode: true,
        max_selection: 999,
        selected_groups: [...company.groups],
        modal_title: 'Select Groups'
      }
    });

    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        const old_groups = JSON.parse(groups_copy);
        this.CheckCompanyGroups(company, old_groups, data.data);
      }
    });

    return await modal.present();
  }

  async CheckCompanyGroups(company: any, old_groups: any[], new_groups: any[]) {
    await this.globalService.presentLoading('Updating Groups...', 9000000);
    old_groups.forEach(group => {
      const index = new_groups.findIndex(e => group.guid === e.guid);
      if (index == -1) {
        this.apiService.delete(`groups/companies/${company.guid}`, group);
      }
    });
    new_groups.forEach(group => {
      const index = old_groups.findIndex(e => group.guid === e.guid);
      if (index == -1) {
        this.apiService.post(`groups/companies/${company.guid}`, group);
      }
    });
    company.groups = [...new_groups];
    this.UpdateGroupsCount();
    this.globalService.dismissLoading();
  }

}
